import type { Session } from '@mesa/core/auth/better-auth';
import { BookBookmarkIcon, BookOpenIcon, GearIcon, KeyIcon, UsersIcon, WebhooksLogoIcon } from '@phosphor-icons/react';
import { Link, useRouterState } from '@tanstack/react-router';
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupContent,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarSeparator,
  useSidebar,
} from '@/components/ui/sidebar';
import type { OrgSummary } from '@/hooks/use-org';
import { OrgSwitcher } from './org-switcher';
import { UserMenu } from './user-menu';

export const navItems: { title: string; path: string; Icon: typeof KeyIcon; deprecated?: boolean }[] = [
  {
    title: 'API Tokens',
    path: 'tokens',
    Icon: KeyIcon,
    deprecated: true,
  },
  {
    title: 'Repositories',
    path: 'repositories',
    Icon: BookBookmarkIcon,
  },
  {
    title: 'Webhooks',
    path: 'webhooks',
    Icon: WebhooksLogoIcon,
  },
  {
    title: 'Members',
    path: 'members',
    Icon: UsersIcon,
  },
  {
    title: 'Settings',
    path: 'settings',
    Icon: GearIcon,
  },
];

const DOCS_URL = 'https://docs.mesa.dev';

interface AppSidebarProps {
  session: Session;
  currentOrg: OrgSummary;
  orgs: OrgSummary[];
  gitBaseUrl: string;
  onOrgUpdated?: () => void;
}

export function AppSidebar({ session, currentOrg, orgs, gitBaseUrl, onOrgUpdated }: AppSidebarProps) {
  const { location } = useRouterState();
  const { isCollapsed } = useSidebar();

  return (
    <Sidebar>
      <SidebarHeader>
        <OrgSwitcher currentOrg={currentOrg} orgs={orgs} gitBaseUrl={gitBaseUrl} onOrgUpdated={onOrgUpdated} />
      </SidebarHeader>
      <SidebarContent>
        <SidebarGroup>
          <SidebarGroupContent>
            <SidebarMenu>
              {navItems.map((item) => {
                const href = `/${currentOrg.slug}/${item.path}`;
                const NavIcon = item.Icon;
                return (
                  <SidebarMenuItem key={item.path}>
                    <SidebarMenuButton asChild isActive={location.pathname === href}>
                      <Link to={href}>
                        <NavIcon size={20} className="shrink-0" />
                        {!isCollapsed && <span>{item.title}</span>}
                        {!isCollapsed && item.deprecated && (
                          <span className="ml-auto text-[10px] font-medium uppercase tracking-wide text-mesa-muted">
                            deprecated
                          </span>
                        )}
                      </Link>
                    </SidebarMenuButton>
                  </SidebarMenuItem>
                );
              })}
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>
      </SidebarContent>
      <SidebarFooter>
        <SidebarMenu>
          <SidebarMenuItem>
            <SidebarMenuButton asChild>
              <a href={DOCS_URL} target="_blank" rel="noopener noreferrer">
                <BookOpenIcon size={20} className="shrink-0" />
                {!isCollapsed && <span>Documentation</span>}
              </a>
            </SidebarMenuButton>
          </SidebarMenuItem>
        </SidebarMenu>
        <SidebarSeparator />
        <UserMenu session={session} />
      </SidebarFooter>
    </Sidebar>
  );
}
